import type { CSSProperties } from 'react';
import { MASCOT_SIZE, type MascotName, mascotUrls } from '../illustrations';
import { cx } from './cx';
import { BLANK_IMAGE } from './media';

export interface MascotProps {
  name: MascotName;
  className?: string;
  style?: CSSProperties;
  /**
   * lazy (default) never delays primary content; pass eager when the sprite
   * is above the fold, as a hero Scene does.
   */
  loading?: 'lazy' | 'eager';
  /**
   * A media query where the sprite is not shown (its container is hidden
   * there). The picture resolves to a blank data URI at that media, so an
   * eager sprite costs nothing where nobody sees it.
   */
  skipMedia?: string;
}

/**
 * A giraffe sprite. Always decorative (alt="" and aria-hidden): the mascot
 * never carries information a page relies on. Half size serves 1x screens and
 * full size 2x. Lazy unless the caller says it is above the fold.
 */
export function Mascot({ name, className, style, loading = 'lazy', skipMedia }: MascotProps) {
  const { x1, x2 } = mascotUrls(name);
  const { width, height } = MASCOT_SIZE[name];
  return (
    <picture className={cx('ee-mascot', className)} style={style} data-mascot={name}>
      {skipMedia ? <source media={skipMedia} srcSet={BLANK_IMAGE} /> : null}
      <source type="image/webp" srcSet={`${x1} 1x, ${x2} 2x`} />
      <img
        src={x1}
        srcSet={`${x1} 1x, ${x2} 2x`}
        width={width}
        height={height}
        alt=""
        aria-hidden="true"
        loading={loading}
        decoding="async"
        draggable={false}
      />
    </picture>
  );
}
